import ChevronDownIcon from "@heroicons/react/24/outline/ChevronDownIcon";
import { FC, useEffect, useState } from "react";
import { createSearchParams, useNavigate } from "react-router-dom";

import {
  ButtonBase,
  ListItemText,
  MenuItem,
  MenuList,
  Popover,
  SvgIcon,
  Typography,
} from "@mui/material";

import { usePopover } from "../../hooks/use-popover";
import useResetRedirect from "../../hooks/use-reset-redirect";
import { setClusterAndOrg } from "../../slices/current";
import { useDispatch, useSelector } from "../../store";
import {
  Organization,
  useListClustersQuery,
  useListOrganizationsQuery,
} from "../../store/pomeriumApi";
import { setOrgClusters } from "@/slices/organizations";

export const OrganizationDropdown: FC = () => {
  const popover = usePopover<HTMLButtonElement>();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { organizationId } = useSelector((state) => state.current);
  const { data: organizations } = useListOrganizationsQuery();
  const baseRedirect = useResetRedirect();
  const [selectedOrgId, setSelectedOrgId] = useState(organizationId);
  const { data: clusters } = useListClustersQuery(selectedOrgId);

  useEffect(() => {
    if (selectedOrgId !== organizationId) {
      if (popover.open) {
        popover.handleClose();
      }
      const cId = clusters?.at(0)?.id || "";
      dispatch(
        setOrgClusters({
          organizationId: selectedOrgId,
          clusters: clusters || [],
        }),
      );
      dispatch(
        setClusterAndOrg({
          clusterId: cId,
          organizationId: selectedOrgId,
        }),
      );
      navigate(
        {
          pathname: baseRedirect,
          search: createSearchParams({
            oid: selectedOrgId,
            cid: cId,
          }).toString(),
        },
        { replace: true },
      );
    }
  }, [clusters]);

  const organization = organizations?.find(
    (organization) => organization.id === organizationId,
  ) as Organization;

  if (!organizations) {
    return <></>;
  }

  return (
    <>
      <ButtonBase
        onClick={popover.handleOpen}
        ref={popover.anchorRef}
        sx={{
          backgroundColor: "background.paper",
          borderRadius: 1,
          border: 2,
          borderColor: "divider",
          display: "flex",
          px: "12px",
          py: "6px",
          "&:hover": {
            backgroundColor: "background.default",
          },
          width: "100%",
          justifyContent: "space-between",
          variant: "outlined",
          maxWidth: 250,
        }}
      >
        <Typography
          component="span"
          color="inherit"
          sx={{
            fontSize: 14,
            lineHeight: "24px",
          }}
        >
          {organization?.organizationType === "professional"
            ? organization?.name
            : "Personal Account"}
        </Typography>
        <SvgIcon color="action" fontSize="small">
          <ChevronDownIcon />
        </SvgIcon>
      </ButtonBase>
      <Popover
        anchorEl={popover.anchorRef.current}
        anchorOrigin={{
          horizontal: "left",
          vertical: "bottom",
        }}
        disableScrollLock
        onClose={popover.handleClose}
        open={popover.open}
        slotProps={{
          paper: {
            sx: {
              p: 1,
              minWidth: 210,
            },
          },
        }}
      >
        <MenuList dense sx={{ p: 0 }}>
          {organizations.map((o) => {
            const isSelected = o.id === organizationId;

            return (
              <MenuItem
                key={"org" + o.id + o.name}
                onClick={() => setSelectedOrgId(o.id)}
                selected={isSelected}
                sx={{ borderRadius: 1 }}
              >
                <ListItemText>
                  {o.organizationType === "professional"
                    ? o.name
                    : "Personal Account"}
                </ListItemText>
              </MenuItem>
            );
          })}
        </MenuList>
      </Popover>
    </>
  );
};
